import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus, Trash2, Copy, ChevronUp, ChevronDown, ArrowUpDown, Save, Loader2, Check,
} from 'lucide-react';
import type { SortSpec, ExportTemplateColumn } from './types';

// ─── Props ──────────────────────────────────────────────────────────

interface SortDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current sort specs */
  sortSpecs: SortSpec[];
  /** Callback when user clicks OK — applies the new sort */
  onApply: (specs: SortSpec[]) => void;
  /** Available columns to sort by (enabled template columns) */
  columns: ExportTemplateColumn[];
  /** Whether a template is selected (for save button) */
  templateId?: string | null;
  /** Callback to persist sort to template */
  onSaveToTemplate?: (specs: SortSpec[]) => Promise<void>;
  /** The saved default sort from the template (to detect dirty state) */
  savedSort?: SortSpec[] | null;
}

// ─── Component ──────────────────────────────────────────────────────

const SortDialog: React.FC<SortDialogProps> = ({
  open, onClose, sortSpecs, onApply, columns,
  templateId, onSaveToTemplate, savedSort,
}) => {
  // Local working copy of sort specs (edited in the dialog, applied on OK)
  const [localSpecs, setLocalSpecs] = useState<SortSpec[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Sync local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalSpecs(sortSpecs.length > 0 ? [...sortSpecs] : []);
      setSelectedIdx(0);
      setSavedFlash(false);
    }
  }, [open, sortSpecs]);

  // Available columns (not already used in sort, except the one at selectedIdx)
  const getAvailableColumns = useCallback((currentIdx: number) => {
    const usedIds = new Set(localSpecs.map((s, i) => i !== currentIdx ? s.colId : null).filter(Boolean));
    return columns.filter(c => c.is_enabled && !usedIds.has(c.id));
  }, [localSpecs, columns]);

  // ─── Handlers ───────────────────────────────────────────────────

  const handleAddLevel = useCallback(() => {
    const usedIds = new Set(localSpecs.map(s => s.colId));
    const nextCol = columns.find(c => c.is_enabled && !usedIds.has(c.id));
    if (!nextCol) return; // All columns already used
    const newSpec: SortSpec = { colId: nextCol.id, direction: 'asc' };
    setLocalSpecs(prev => [...prev, newSpec]);
    setSelectedIdx(localSpecs.length);
  }, [localSpecs, columns]);

  const handleDeleteLevel = useCallback(() => {
    if (localSpecs.length === 0) return;
    setLocalSpecs(prev => {
      const next = prev.filter((_, i) => i !== selectedIdx);
      return next;
    });
    setSelectedIdx(prev => Math.max(0, Math.min(prev, localSpecs.length - 2)));
  }, [localSpecs, selectedIdx]);

  const handleCopyLevel = useCallback(() => {
    if (localSpecs.length === 0 || selectedIdx >= localSpecs.length) return;
    const source = localSpecs[selectedIdx];
    // Find next unused column for the copy
    const usedIds = new Set(localSpecs.map(s => s.colId));
    const nextCol = columns.find(c => c.is_enabled && !usedIds.has(c.id));
    if (!nextCol) return;
    const copy: SortSpec = { colId: nextCol.id, direction: source.direction };
    setLocalSpecs(prev => {
      const next = [...prev];
      next.splice(selectedIdx + 1, 0, copy);
      return next;
    });
    setSelectedIdx(selectedIdx + 1);
  }, [localSpecs, selectedIdx, columns]);

  const handleMoveUp = useCallback(() => {
    if (selectedIdx <= 0) return;
    setLocalSpecs(prev => {
      const next = [...prev];
      [next[selectedIdx - 1], next[selectedIdx]] = [next[selectedIdx], next[selectedIdx - 1]];
      return next;
    });
    setSelectedIdx(prev => prev - 1);
  }, [selectedIdx]);

  const handleMoveDown = useCallback(() => {
    if (selectedIdx >= localSpecs.length - 1) return;
    setLocalSpecs(prev => {
      const next = [...prev];
      [next[selectedIdx], next[selectedIdx + 1]] = [next[selectedIdx + 1], next[selectedIdx]];
      return next;
    });
    setSelectedIdx(prev => prev + 1);
  }, [selectedIdx, localSpecs.length]);

  const handleColumnChange = useCallback((idx: number, colId: string) => {
    setLocalSpecs(prev => prev.map((s, i) => i === idx ? { ...s, colId } : s));
  }, []);

  const handleDirectionChange = useCallback((idx: number, direction: 'asc' | 'desc') => {
    setLocalSpecs(prev => prev.map((s, i) => i === idx ? { ...s, direction } : s));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localSpecs);
    onClose();
  }, [localSpecs, onApply, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // ─── Save to Template ─────────────────────────────────────────────

  const isDirtyVsSaved = useMemo(() => {
    const saved = savedSort || [];
    if (localSpecs.length !== saved.length) return true;
    return localSpecs.some((s, i) => s.colId !== saved[i]?.colId || s.direction !== saved[i]?.direction);
  }, [localSpecs, savedSort]);

  const handleSaveToTemplate = useCallback(async () => {
    if (!onSaveToTemplate) return;
    setSaving(true);
    try {
      await onSaveToTemplate(localSpecs);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  }, [onSaveToTemplate, localSpecs]);

  const enabledCols = useMemo(() => columns.filter(c => c.is_enabled), [columns]);
  const canAddMore = localSpecs.length < enabledCols.length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[640px] w-full p-0 gap-0">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-primary" />
            <DialogTitle className="text-base font-semibold">Sort</DialogTitle>
          </div>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            Define how the output preview rows are sorted. Add multiple levels for multi-column sorting.
          </DialogDescription>
        </DialogHeader>

        {/* ─── Toolbar ─── */}
        <div className="px-5 py-2.5 border-b border-border/50 flex items-center gap-2 bg-secondary/20">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddLevel}
                  disabled={!canAddMore}
                  className="h-7 text-xs border-border text-muted-foreground hover:text-foreground gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Level
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Add a new sort level</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDeleteLevel}
                  disabled={localSpecs.length === 0}
                  className="h-7 text-xs border-border text-red-400/70 hover:text-red-400 hover:border-red-700/50 gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Level
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Remove the selected sort level</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyLevel}
                  disabled={localSpecs.length === 0 || !canAddMore}
                  className="h-7 text-xs border-border text-muted-foreground hover:text-foreground gap-1"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy Level
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Duplicate the selected sort level</TooltipContent>
            </Tooltip>

            <div className="w-px h-5 bg-border mx-1" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMoveUp}
                  disabled={selectedIdx <= 0 || localSpecs.length === 0}
                  className="h-7 w-7 p-0 border-border text-muted-foreground hover:text-foreground"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Move selected level up</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMoveDown}
                  disabled={selectedIdx >= localSpecs.length - 1 || localSpecs.length === 0}
                  className="h-7 w-7 p-0 border-border text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Move selected level down</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* ─── Sort Levels Table ─── */}
        <div className="px-5 py-3 min-h-[180px] max-h-[360px] overflow-y-auto">
          {localSpecs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <ArrowUpDown className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">No sort levels defined.</p>
              <p className="text-xs mt-1 opacity-60">Click "Add Level" to start sorting.</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-[80px]"></th>
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Column</th>
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-[140px]">Sort On</th>
                  <th className="text-left py-2 px-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider w-[140px]">Order</th>
                </tr>
              </thead>
              <tbody>
                {localSpecs.map((spec, idx) => {
                  const isSelected = idx === selectedIdx;
                  const availableCols = getAvailableColumns(idx);
                  const currentCol = enabledCols.find(c => c.id === spec.colId);

                  return (
                    <tr
                      key={idx}
                      onClick={() => setSelectedIdx(idx)}
                      className={`border-b border-border/30 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary/10 border-l-2 border-l-primary'
                          : 'hover:bg-secondary/30'
                      }`}
                    >
                      {/* Label */}
                      <td className="py-2 px-2">
                        <span className={`text-[11px] font-semibold ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                          {idx === 0 ? 'Sort by' : 'Then by'}
                        </span>
                      </td>

                      {/* Column Picker */}
                      <td className="py-2 px-2">
                        <Select
                          value={spec.colId}
                          onValueChange={(v) => handleColumnChange(idx, v)}
                        >
                          <SelectTrigger className="h-8 text-xs bg-background border-border">
                            <SelectValue placeholder="Select column...">
                              {currentCol?.export_column_name || 'Select column...'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[240px]">
                            {/* Show current column first if it's not in available list */}
                            {currentCol && !availableCols.find(c => c.id === currentCol.id) && (
                              <SelectItem key={currentCol.id} value={currentCol.id}>
                                {currentCol.export_column_name}
                              </SelectItem>
                            )}
                            {availableCols.map(col => (
                              <SelectItem key={col.id} value={col.id}>
                                {col.export_column_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Sort On (always Cell Values for now) */}
                      <td className="py-2 px-2">
                        <Select value="values" disabled>
                          <SelectTrigger className="h-8 text-xs bg-background border-border opacity-70">
                            <SelectValue>Cell Values</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="values">Cell Values</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Order */}
                      <td className="py-2 px-2">
                        <Select
                          value={spec.direction}
                          onValueChange={(v) => handleDirectionChange(idx, v as 'asc' | 'desc')}
                        >
                          <SelectTrigger className="h-8 text-xs bg-background border-border">
                            <SelectValue>
                              {spec.direction === 'asc' ? 'A to Z' : 'Z to A'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">A to Z</SelectItem>
                            <SelectItem value="desc">Z to A</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ─── Footer ─── */}
        <DialogFooter className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {/* Save to Template button */}
            {onSaveToTemplate && templateId && (
              savedFlash ? (
                <span className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-900/20 border border-green-700/30 text-xs text-green-400 font-medium">
                  <Check className="w-3.5 h-3.5" />
                  Saved to Template
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSaveToTemplate}
                  disabled={saving || !isDirtyVsSaved}
                  className={`h-8 text-xs gap-1 ${
                    isDirtyVsSaved
                      ? 'border-amber-600/50 text-amber-400 hover:bg-amber-900/20 hover:border-amber-500/60'
                      : 'border-border text-muted-foreground/40'
                  }`}
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  {isDirtyVsSaved ? 'Save to Template' : 'Saved'}
                </Button>
              )
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              className="h-8 text-xs text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              className="h-8 text-xs golden-button"
            >
              OK
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SortDialog;
